/** Add / edit a portfolio project, then upload its photos (any number, tagged Before / During / Finished…). */
import React, { useEffect, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { CATEGORY_LABEL } from './ProjectCard';

const EMPTY = { title: '', address: '', installation_date: '', square_metres: '', material_used: '', mix_or_blend: '', description: '' };

export const MARKETING_NOTE = 'Photos you add here may be used by Ox Resin for marketing (website, social media and brochures). Please only upload images you are happy for us to share.';

async function req(path, opts) {
  const res = await fetch(`${API_URL}/api${path}`, { credentials: 'include', ...opts });
  if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Request failed');
  return res.json();
}

export function ProjectDialog({ open, onClose, brand, theme, project, labels = {}, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cat, setCat] = useState('finished');
  const [uploading, setUploading] = useState(0);
  useEffect(() => {
    if (!open) return;
    setSaved(project || null);
    setForm(project ? { title: project.title || '', address: project.address || '', installation_date: project.installation_date || '', square_metres: project.square_metres ?? '', material_used: project.material_used || '', mix_or_blend: project.mix_or_blend || '', description: project.description || '' } : EMPTY);
  }, [open, project]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) return toast.error('Give the project a name.');
    setBusy(true);
    try {
      const body = JSON.stringify({ ...form, brand, square_metres: form.square_metres === '' ? null : Number(form.square_metres) });
      const d = saved ? await req(`/installer/portfolio/${saved.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body }) : await req('/installer/portfolio', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      setSaved(d); onSaved?.(d);
      toast.success(saved ? 'Project updated.' : 'Project added — now add your photos.');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const upload = async (files) => {
    if (!saved || !files?.length) return;
    setUploading(files.length);
    let latest = saved;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append('category', file.type.startsWith('video/') ? 'video' : cat);
      fd.append('file', file);
      try { latest = await req(`/installer/portfolio/${saved.id}/photos`, { method: 'POST', body: fd }); } catch (e) { toast.error(`${file.name}: ${e.message}`); }
      setUploading((n) => n - 1);
    }
    setSaved(latest); onSaved?.(latest);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="portfolio-dialog">
        <DialogHeader><DialogTitle>{saved ? 'Edit project' : 'Add a portfolio project'}</DialogTitle><DialogDescription>Ox Resin can see every project you add — it builds your track record with us.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Project name *" className="sm:col-span-2"><Input value={form.title} onChange={set('title')} placeholder="e.g. Driveway, Smith residence" data-testid="pf-title" /></F>
            <F label="Installation date"><Input type="date" value={form.installation_date} onChange={set('installation_date')} data-testid="pf-date" /></F>
            <F label="Square metres"><Input type="number" min="0" step="0.1" value={form.square_metres} onChange={set('square_metres')} data-testid="pf-area" /></F>
            <F label="Address / location" className="sm:col-span-2"><Input value={form.address} onChange={set('address')} placeholder="Town or full address" data-testid="pf-address" /></F>
            <F label={labels.material_used_label || 'Material used'}><Input value={form.material_used} onChange={set('material_used')} data-testid="pf-material" /></F>
            <F label={labels.mix_or_blend_label || 'Mix / blend / colour'}><Input value={form.mix_or_blend} onChange={set('mix_or_blend')} data-testid="pf-blend" /></F>
            <F label="Notes" className="sm:col-span-2"><Textarea rows={3} value={form.description} onChange={set('description')} placeholder="Anything worth knowing — prep, challenges, what the customer said…" data-testid="pf-description" /></F>
          </div>
          <Button onClick={save} disabled={busy} className="w-full font-semibold" style={{ background: theme.buttonBg, color: theme.buttonText }} data-testid="pf-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{saved ? 'Save changes' : 'Save project & continue to photos'}</Button>

          <div className={`rounded-xl border-2 border-dashed p-3 space-y-2 ${saved ? 'border-gray-300' : 'border-gray-200 opacity-50 pointer-events-none'}`} data-testid="pf-photos">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-gray-800">Photos &amp; video</span>
              <span className="text-[11px] text-gray-500">{saved ? `${(saved.photos || []).length} photo${(saved.photos || []).length === 1 ? '' : 's'}${saved.video ? ' + video' : ''}` : 'save the project first'}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 border rounded-md px-2 bg-white text-xs" data-testid="pf-photo-category">
                {Object.entries(CATEGORY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold cursor-pointer" style={{ background: theme.buttonBg, color: theme.buttonText }}>
                {uploading > 0 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading > 0 ? `Uploading ${uploading}…` : 'Choose photos'}
                <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid="pf-photo-input" />
              </label>
            </div>
            {saved && (saved.photos || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {saved.photos.map((ph) => <img key={ph.id} src={`${API_URL}${ph.url}`} alt={ph.category} className="w-16 h-16 object-cover rounded-md border" />)}
              </div>
            )}
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="pf-marketing-note">{MARKETING_NOTE}</p>
          </div>
          {saved && <Button variant="outline" onClick={onClose} className="w-full" data-testid="pf-done">Done</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children, className = '' }) {
  return <div className={className}><label className="text-xs font-bold text-gray-700">{label}</label><div className="mt-1">{children}</div></div>;
}
